import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';
const AuthContext = createContext(undefined);
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    // Function to refresh user data
    const refreshUser = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                console.log('No token found during refreshUser');
                throw new Error('No token found');
            }
            console.log('Refreshing user data with token');
            const response = await api.get('/auth/me');
            console.log('User data refreshed:', response.data);
            setUser(response.data.user);
            return response.data.user;
        }
        catch (error) {
            console.error('Error refreshing user data:', error);
            throw error;
        }
    };
    useEffect(() => {
        const checkAuth = async () => {
            try {
                const token = localStorage.getItem('token');
                if (!token) {
                    console.log('No token found during initial auth check');
                    throw new Error('No token found');
                }
                console.log('Checking auth with token');
                const response = await api.get('/auth/me');
                console.log('Auth check successful:', response.data);
                setUser(response.data.user);
            }
            catch (error) {
                console.error('Auth check failed:', error);
                setUser(null);
                localStorage.removeItem('token');
            }
            finally {
                setIsLoading(false);
            }
        };
        checkAuth();
    }, []);
    const login = async (email, password) => {
        console.log('Login attempt for:', email);
        try {
            const response = await api.post('/auth/login', {
                email,
                password,
            });
            console.log('Login successful:', response.data);
            const { user, token } = response.data;
            localStorage.setItem('token', token);
            setUser(user);
        }
        catch (error) {
            console.error('Login failed:', error);
            throw error;
        }
    };
    const register = async (email, password, firstName, lastName) => {
        try {
            const response = await api.post('/auth/register', {
                email,
                password,
                firstName,
                lastName,
            });
            console.log('Registration successful:', response.data);
            const { user, token } = response.data;
            localStorage.setItem('token', token);
            setUser(user);
        }
        catch (error) {
            console.error('Registration failed:', error);
            throw error;
        }
    };
    const logout = async () => {
        try {
            console.log('Logging out');
            await api.post('/auth/logout');
        }
        catch (error) {
            console.error('Logout error (continuing):', error);
        }
        finally {
            localStorage.removeItem('token');
            setUser(null);
            console.log('Logged out successfully');
        }
    };
    const value = {
        user,
        isAuthenticated: !!user,
        login,
        register,
        logout,
        refreshUser,
        isLoading,
    };
    if (isLoading) {
        return _jsx("div", { children: "Loading..." });
    }
    return _jsx(AuthContext.Provider, { value: value, children: children });
};
export default AuthContext;
